import React from 'react';
import { message } from 'antd';
import { CreditCard } from '@phosphor-icons/react';
import type { CombinedFinancingSummary } from './financingCombinedSummary';
import './oneButton-css/FinancingCombinedView.css';

export type FinancingCombinedViewProps = {
    summary: CombinedFinancingSummary;
    loading?: boolean;
};

export type FinancingDealHealthChipProps = {
    monthlyDisplay: string;
    className?: string;
};

/** Pill shown in Financing Options header (combined monthly total). */
export function FinancingDealHealthChip({ monthlyDisplay, className }: FinancingDealHealthChipProps) {
    const showComingSoon = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        message.info('Deal Health — coming soon.');
    };

    return (
        <button
            type="button"
            className={`obd-financing-combined__deal-health${className ? ` ${className}` : ''}`}
            aria-label={`Deal health ${monthlyDisplay}. Coming soon.`}
            title="Deal Health (coming soon)"
            onClick={showComingSoon}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') showComingSoon(e);
            }}
        >
            <span className="obd-financing-combined__deal-health-label">Deal Health</span>
            <span className="obd-financing-combined__deal-health-value">{monthlyDisplay}</span>
        </button>
    );
}

function partnerAccentClass(partner: string): string {
    const k = partner.toLowerCase().replace(/\s+/g, '');
    if (k.includes('lightreach')) return 'lightreach';
    if (k.includes('goodleap')) return 'goodleap';
    if (k === 'cash') return 'cash';
    if (k.startsWith('igs')) return 'igs';
    return 'default';
}

export const FinancingCombinedView: React.FC<FinancingCombinedViewProps> = ({ summary, loading = false }) => {
    const totalDisplay =
        summary.totalMonthly > 0 ? `$${summary.totalMonthly.toFixed(2)}/mo` : loading ? '…' : '—';

    return (
        <div className="obd-financing-combined" aria-label="Combined financing summary">
            <div className="obd-financing-combined__head">
                <div className="obd-financing-combined__head-main">
                    <span className="obd-financing-combined__head-icon" aria-hidden>
                        <CreditCard size={20} weight="duotone" />
                    </span>
                    <div>
                        <p className="obd-financing-combined__label">Combined Financing</p>
                        <p className="obd-financing-combined__total">{totalDisplay}</p>
                        <p className="obd-financing-combined__breakdown">{summary.breakdown}</p>
                    </div>
                </div>
            </div>

            <div className="obd-financing-combined__lines">
                {summary.lines.length === 0 ? (
                    <p className="obd-financing-combined__empty">
                        {loading
                            ? 'Loading pricing…'
                            : 'No recommended pricing yet. Open Best pricing options or refresh pricing.'}
                    </p>
                ) : (
                    summary.lines.map((line) => (
                        <article key={line.id} className="obd-financing-combined__line">
                            <div className="obd-financing-combined__line-head">
                                <span
                                    className={`obd-financing-combined__line-dot obd-financing-combined__line-dot--${partnerAccentClass(line.partner)}`}
                                    aria-hidden
                                />
                                <div className="obd-financing-combined__line-copy">
                                    <p className="obd-financing-combined__line-title">
                                        {line.partnerLabel}
                                        {line.method ? ` · ${line.method}` : ''}
                                    </p>
                                    <p className="obd-financing-combined__line-monthly">
                                        ${line.monthly.toFixed(2)}/mo
                                    </p>
                                    <p className="obd-financing-combined__line-detail">
                                        {line.productLabel}
                                        {line.detail ? ` · ${line.detail}` : ''}
                                    </p>
                                </div>
                            </div>
                        </article>
                    ))
                )}
            </div>
        </div>
    );
};

export default FinancingCombinedView;
